import { motion } from "motion/react";
import { RadioGroup as Primitive } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "../../../cn.ts";

/**
 * § Primitives, SegmentedControl: a radio group drawn as one track of options with the checked one
 * raised, Spectrum UI's Profile Dropdown theme control (`@spectrumui/profile-dropdown`, Apache-2.0)
 * themed through the variables of `packages/tokens`. The account card's theme choice and the
 * preferences page's theme, motion and density choices draw it (2026-09-17, by the owner's decision),
 * so a small closed choice reads the same wherever it stands. Each option is a Radix radio, so a
 * screen reader hears a radio group; the press scale is the source's spring, which reduced motion stills.
 *
 * `size` `sm` (2026-10-03, by the owner's approval of the product review, which found full-width
 * segmented tabs for a two- or three-way choice): the compact control, as wide as its options and no
 * wider (about 200 px for two or three short words), at the small text size and a 28 px option, for a
 * setting's choice in a form (Theme, Motion, Density, an insight's importance, a task's answer when a
 * client moves on). `md`, the default, keeps the account card's and a view's switcher's size.
 */
export interface SegmentedOption<V extends string> {
  readonly value: V;
  readonly label: ReactNode;
  readonly icon?: ReactNode;
}

export interface SegmentedControlProps<V extends string> {
  readonly options: readonly SegmentedOption<V>[];
  /** The checked option, or null while nothing is chosen yet (the stage move's per-task answer, 020 FR-020-268). */
  readonly value: V | null;
  readonly onValueChange: (value: V) => void;
  readonly label: string;
  readonly disabled?: boolean;
  /** `sm` is the compact control sized to its options; `md` the default. */
  readonly size?: "sm" | "md";
  readonly className?: string;
}

export function SegmentedControl<V extends string>({ options, value, onValueChange, label, disabled = false, size = "md", className }: SegmentedControlProps<V>) {
  const compact = size === "sm";
  return (
    <Primitive.Root
      data-slot="segmented-control"
      data-size={size}
      className={cn("inline-flex gap-(--space-1) rounded-lg bg-muted p-(--space-1)", compact && "w-fit max-w-full self-start", className)}
      aria-label={label}
      orientation="horizontal"
      value={value ?? ""}
      disabled={disabled}
      onValueChange={(next) => {
        onValueChange(next as V);
      }}
    >
      {options.map((option) => (
        <Primitive.Item
          key={option.value}
          value={option.value}
          className={cn("cursor-pointer rounded-md disabled:cursor-not-allowed disabled:opacity-50 text-sm text-text-tertiary outline-none focus-visible:ring-(length:--focus-ring-width) focus-visible:ring-focus-ring data-[state=checked]:bg-background data-[state=checked]:font-medium data-[state=checked]:text-text-primary data-[state=checked]:shadow-xs", compact ? "text-small" : "flex-1")}
        >
          <motion.span whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 17 }} className={cn("flex items-center justify-center gap-(--space-2) px-(--space-3) whitespace-nowrap [&_svg]:size-(--icon-sm)", compact ? "min-h-(--chip-height-md) py-(--space-1)" : "py-(--space-2)")}>
            {option.icon !== undefined && (
              <span className="flex" aria-hidden="true">
                {option.icon}
              </span>
            )}
            {option.label}
          </motion.span>
        </Primitive.Item>
      ))}
    </Primitive.Root>
  );
}
